import React, { useState } from "react";
import { PatientTaskInstanceDocument, PatientTaskInstanceId } from "../../messages";
import { isLoading } from "../../Loading";
import { AppTasksState } from "../../system/AppState/AppTasksState";

interface Params {
  tasksState: AppTasksState;
  onClickFixDocuments: (taskInstanceId: PatientTaskInstanceId | null) => void;
}

function getTotalDocumentsWithRequiredFixes(tasksState: AppTasksState) {
  if (tasksState.tasks.length() === 0) {
    return 0;
  }

  return tasksState.tasks.toArray().reduce((count, task) => {
    return (
      count +
      task.taskDetails.documents.filter(
        (doc: PatientTaskInstanceDocument) =>
          doc.type === "PatientTaskInstanceDocumentRegular" && doc.isOpenForResubmission
      ).length
    );
  }, 0);
}

export function useDocumentsRequireFixedBottomSheet({ onClickFixDocuments, tasksState }: Params) {
  const [isDocumentsRequireFixedBottomSheetOpen, setDocumentsRequireFixedBottomSheetOpen] =
    useState(getTotalDocumentsWithRequiredFixes(tasksState) > 0);

  const singleDocumentWithFixesTaskInstanceId = React.useMemo(() => {
    if (isLoading(tasksState)) {
      return null;
    }

    if (tasksState.tasks.length() !== 1) {
      return null;
    }

    const task = tasksState.tasks.toArray()[0];
    return task.id;
  }, [tasksState]);

  const totalDocumentsWithRequiredFixes = React.useMemo(() => {
    if (isLoading(tasksState)) {
      return 0;
    }
    return getTotalDocumentsWithRequiredFixes(tasksState);
  }, [tasksState]);

  const handleClickFixDocuments = React.useCallback(() => {
    setDocumentsRequireFixedBottomSheetOpen(false);
    onClickFixDocuments(singleDocumentWithFixesTaskInstanceId);
  }, [singleDocumentWithFixesTaskInstanceId, onClickFixDocuments]);

  const handleRequestClose = React.useCallback(() => {
    setDocumentsRequireFixedBottomSheetOpen(false);
  }, []);

  return {
    isOpen: isDocumentsRequireFixedBottomSheetOpen,
    handleRequestClose,
    totalDocumentsWithRequiredFixes,
    isDocumentsRequireFixedBottomSheetOpen,
    handleClickFixDocuments,
    documentsAmount: totalDocumentsWithRequiredFixes,
  };
}
